import { ChevronLeft, ChevronRight } from "lucide-react"
import { LazyLoadImage } from "react-lazy-load-image-component"
import { NavLink } from "react-router-dom"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { menuItems } from "@/config/menu"
import { useLogout } from "@/features/auth/hooks/use-logout"
import { useAuthStore } from "@/features/auth/store/auth-store"
import { isAdminRole, isMasterRole } from "@/config/roles"
import { useCompanyStore } from "@/features/company/store/company-store"
import { cn } from "@/utils"
import { useUIStore } from "@/store/ui-store"

import { Icon } from "./icon"

export function Menu() {
  const { company } = useCompanyStore()
  const { isSidebarCollapsed, toggleSidebar } = useUIStore()
  const { handleLogout } = useLogout()
  const { user } = useAuthStore()
  const isAdmin = isAdminRole(user)
  const isMaster = isMasterRole(user)

  const filteredMenuItems = menuItems.filter((item) => {
    if (!item.active) return false
    if (item.adminOnly && !(isAdmin || isMaster)) return false
    if (item.roles.length === 0) return true // Visível para todos os autenticados
    return user?.user_roles ? item.roles.includes(Number(user.user_roles)) : false
  })

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-background-subtle bg-background-emphasis transition-all duration-300",
        isSidebarCollapsed ? "w-20" : "w-56",
      )}
    >
      {/* 1. Logo */}
      <div className="flex h-32 w-full items-center justify-center border-b border-background-subtle p-4">
        {company?.logo ? (
          <LazyLoadImage
            src={company.logo}
            alt={`Logo ${company.fantasy_name}`}
            className="h-20 object-contain"
            effect="blur"
          />
        ) : (
          <div className="h-20 w-full animate-pulse rounded-md bg-background-subtle" />
        )}
      </div>

      <div className="flex flex-1 flex-col justify-between">
        <nav className="flex-1 space-y-2 pt-2">
          {/* 3. Itens de Menu */}
          <TooltipProvider delayDuration={0}>
            {filteredMenuItems.map((item) => (
              item.action === "logout" ? (
                <Tooltip key={item.name}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-destructive transition-all hover:bg-background-subtle",
                        isSidebarCollapsed && "justify-center",
                      )}
                    >
                      <Icon icon={item.icon} className="h-5 w-5" />
                      <span
                        className={cn(
                          "overflow-hidden transition-all",
                          isSidebarCollapsed ? "w-0" : "w-auto",
                        )}
                      >
                        {item.name}
                      </span>
                    </button>
                  </TooltipTrigger>
                  {isSidebarCollapsed && (
                    <TooltipContent side="right">{item.name}</TooltipContent>
                  )}
                </Tooltip>
              ) : (
                <Tooltip key={item.name}>
                  <TooltipTrigger asChild>
                    <NavLink
                      to={item.link}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-text transition-all hover:bg-background-subtle",
                        "data-[active=true]:bg-primary data-[active=true]:text-black data-[active=true]:hover:bg-secondary",
                        isSidebarCollapsed && "justify-center",
                      )}
                    >
                      <Icon icon={item.icon} className="h-5 w-5" />
                      <span
                        className={cn(
                          "overflow-hidden transition-all",
                          isSidebarCollapsed ? "w-0" : "w-auto",
                        )}
                      >
                        {item.name}
                      </span>
                    </NavLink>
                  </TooltipTrigger>
                  {isSidebarCollapsed && (
                    <TooltipContent side="right">{item.name}</TooltipContent>
                  )}
                </Tooltip>
              )
            ))}
          </TooltipProvider>
        </nav>

        {/* 2. Perfil do Usuário e Botão de Recolher */}
        <div className="border-t border-background-subtle py-4 px-2">
          <div
            className={cn(
              "flex items-center gap-3",
              isSidebarCollapsed && "justify-center",
            )}
          >
            <Avatar>
              <LazyLoadImage
                src={user?.photo ?? undefined}
                alt={user?.name}
                className="aspect-square h-full w-full"
              />
              <AvatarFallback>
                {user?.name?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span
              className={cn(
                "overflow-hidden text-sm font-medium transition-all",
                isSidebarCollapsed ? "w-0" : "w-auto",
              )}
            >
              {user?.name}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className={cn("ml-auto", isSidebarCollapsed && "hidden")}
              onClick={toggleSidebar}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(!isSidebarCollapsed && "hidden")}
              onClick={toggleSidebar}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </aside>
  )
}